import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ISanityImage } from '@rbi-ctg/menu';
import { Nutrition } from 'components/nutrition';
import { Allergens } from 'components/nutrition/allergens';
import { ProductWeight } from 'components/nutrition/product-weight';
import { AllergenGroup, NutritionFacts } from 'components/nutrition/types';
import { getAllergenGroups } from 'components/nutrition/utils';
import { IsoCountryCode2Char } from 'generated/graphql-gateway';
import { useFormatCalories } from 'hooks/use-format-calories';
import { useFormatJoules } from 'hooks/use-format-joules';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { brandFont } from 'styles/brand-font';
import { primitive } from 'styles/constants/primitives';
import { getCountry } from 'utils/environment';

import { Card, Title } from './card';
import { IProduct } from './types';

const Body = styled.div`
  font: ${brandFont.copyOne};
  line-height: inherit;
  text-transform: ${Styles.textTransform.body};
  color: ${p => p.theme.token('text-default')};
  margin-block-start: ${primitive.$spacing2};

  ${Styles.desktop} {
    margin-block-start: ${primitive.$spacing1};
  }
`;

const CaloriesContainer = styled.p`
  margin: 0;
  font: ${brandFont.copyTwo};
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-block-start: ${primitive.$spacing2};
  padding-block-end: 0;
  padding-inline: ${primitive.$spacing3};

  ${Styles.desktop} {
    padding-block-start: ${primitive.$spacing3};
    padding-block-end: 0;
    padding-inline: ${primitive.$spacing4};
  }
`;

interface IProductCard {
  product: IProduct;
  backgroundImage?: ISanityImage;
}

export const ProductCard: React.FC<IProductCard> = ({ product, backgroundImage }) => {
  const { formatMessage } = useIntl();
  const configCountry = getCountry();
  const enableAllergens = useFlag(LaunchDarklyFlag.ENABLE_NUTRITION_EXPLORER_ALLERGENS);
  const enableEUNutritionDisplay = useFlag(LaunchDarklyFlag.ENABLE_EU_NUTRITION_DISPLAY);
  const formatCalories = useFormatCalories();
  const formatJoules = useFormatJoules();

  const nutritionText = useMemo(() => {
    const formattedCalories = formatCalories(product.nutrition.calories);
    const formattedJoules = formatJoules(product.nutrition.calories);

    if (!enableEUNutritionDisplay) {
      return formattedCalories;
    }

    return formatMessage(
      { id: 'euNutritionValues' },
      { calories: formattedCalories, joules: formattedJoules }
    );
  }, [
    formatMessage,
    formatCalories,
    formatJoules,
    product.nutrition.calories,
    enableEUNutritionDisplay,
  ]);

  let allergenGroups: AllergenGroup[] | undefined;
  if (enableAllergens && product.allergens) {
    allergenGroups = getAllergenGroups(product.allergens, formatMessage);
  }

  const isConfigCountryGermany = configCountry.toUpperCase() === IsoCountryCode2Char.DE;

  const nutritionFacts: NutritionFacts[] = [
    {
      itemName: product.name,
      itemImage: product.image,
      facts: product.nutrition || {},
      allergenGroups,
      additionalItemInformation: product.additionalItemInformation || null,
      productSize: isConfigCountryGermany ? product.productSize : undefined,
    },
  ];

  const nutritionInfoCard = (
    <Card
      key={product._id}
      aria-labelledby={product._id}
      image={product.image}
      imageAlt=""
      backgroundImage={backgroundImage}
    >
      <Content>
        <Title id={product._id} useFullHeight>
          {product.name}
        </Title>
        <Body>
          {nutritionText && <CaloriesContainer>{nutritionText}</CaloriesContainer>}

          {isConfigCountryGermany && <ProductWeight productSize={product.productSize} />}
          {allergenGroups && <Allergens allergenGroups={allergenGroups} />}
        </Body>
      </Content>
    </Card>
  );

  return <Nutrition nutritionFacts={nutritionFacts} activatingElement={nutritionInfoCard} />;
};
